import { useEffect, useState } from 'react';
import { closeSession, listenToOpenSessions, type OpenSession } from '@core/firebase/sessions';
import { isAttended } from '@core/session/presence';
import { useLanguage } from '@/contexts/LanguageContext';
import { ConfirmButton } from '@/components/ConfirmButton';
import { Radio } from 'lucide-react';
import {
  EmptyState, IconTile, LimePill, Notice, PageHeader, SoftCard,
} from '@/components/PageKit';

/**
 * Every session that is open right now.
 *
 * Deliberately the opposite of the student list: unattended sessions are not
 * filtered out, they are BADGED, because a session nobody is attending is
 * exactly what an admin is here to find and close.
 */
export default function SessionsPage() {
  const { t } = useLanguage();
  const [sessions, setSessions] = useState<OpenSession[]>([]);
  const [errorKey, setErrorKey] = useState<string | null>(null);

  useEffect(
    () => listenToOpenSessions(setSessions, () => setErrorKey('admin.sessions.failed')),
    []
  );

  const close = (id: string) => {
    setErrorKey(null);
    void closeSession(id).catch(() => setErrorKey('admin.sessions.failed'));
  };

  const count = (n: number) => new Intl.NumberFormat('en-US').format(n);

  return (
    <div className="mx-auto w-full max-w-6xl space-y-6">
      <PageHeader title={t('admin.sessions.title')} subtitle={t('admin.sessions.subtitle')} />

      {errorKey && <Notice tone="danger" testId="sessions-error">{t(errorKey)}</Notice>}

      {sessions.length === 0 ? (
        <SoftCard as="div">
          <EmptyState
            testId="no-sessions"
            icon={<Radio />}
            title={t('admin.sessions.emptyTitle')}
            body={t('admin.sessions.emptyBody')}
          />
        </SoftCard>
      ) : (
        <section className="space-y-3">
          {/* Board a3: what is live, first. */}
          <h2 className="text-lg font-semibold text-ink">{t('admin.sessions.liveHeading')}</h2>
          <ul className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {sessions.map((s) => {
              const name = s.muqriName || s.muqriId;
              const attended = isAttended(s, Date.now());
              return (
                <li
                  key={s.id}
                  data-testid={`admin-session-${s.id}`}
                  className="card-soft flex flex-col gap-4 p-5"
                >
                  <div className="flex items-start gap-3">
                    <IconTile><Radio /></IconTile>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-[17px] font-semibold text-ink">{name}</p>
                      <p data-testid={`admin-waiting-${s.id}`} className="text-sm text-helper">
                        {t(s.mode === 'halaqa' ? 'muqri.modeHalaqa' : 'muqri.modePrivate')}
                        {' · '}
                        {t('admin.sessions.waiting', { count: count(s.waitingCount) })}
                      </p>
                    </div>
                    {/* A session nobody is attending is exactly what an admin
                        is here to find, so it is badged, not hidden. */}
                    {attended ? (
                      <LimePill>{t('muqri.live')}</LimePill>
                    ) : (
                      <span
                        data-testid={`unattended-${s.id}`}
                        className="shrink-0 rounded-full bg-danger/10 px-3 py-1 text-xs font-semibold text-danger"
                      >
                        {t('muqri.statusClosed')}
                      </span>
                    )}
                  </div>
                  <div className="mt-auto">
                    <ConfirmButton
                      testId={`close-${s.id}`}
                      tone="danger"
                      size="sm"
                      label={t('admin.sessions.close')}
                      question={t('admin.sessions.confirmClose', { name })}
                      onConfirm={() => close(s.id)}
                    />
                  </div>
                </li>
              );
            })}
          </ul>
        </section>
      )}
    </div>
  );
}
